import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FaRocket, FaMobileAlt, FaBuilding, FaLightbulb, FaCogs, FaCheckCircle } from 'react-icons/fa';
import { MdPeople, MdSchool, MdTimeline } from 'react-icons/md';

const FuturePlans = () => {
    const { t } = useTranslation();

    const plans = [
        {
            icon: <FaMobileAlt />,
            titleKey: 'futurePlans.digital.title',
            descKey: 'futurePlans.digital.desc',
            features: [
                t('futurePlans.digital.feature1'),
                t('futurePlans.digital.feature2'),
                t('futurePlans.digital.feature3'),
                t('futurePlans.digital.feature4')
            ],
            timeline: t('futurePlans.digital.timeline')
        },
        {
            icon: <FaBuilding />,
            titleKey: 'futurePlans.expansion.title',
            descKey: 'futurePlans.expansion.desc',
            features: [
                t('futurePlans.expansion.feature1'),
                t('futurePlans.expansion.feature2'),
                t('futurePlans.expansion.feature3'),
                t('futurePlans.expansion.feature4')
            ],
            timeline: t('futurePlans.expansion.timeline')
        },
        {
            icon: <FaLightbulb />,
            titleKey: 'futurePlans.products.title',
            descKey: 'futurePlans.products.desc',
            features: [
                t('futurePlans.products.feature1'),
                t('futurePlans.products.feature2'),
                t('futurePlans.products.feature3'),
                t('futurePlans.products.feature4')
            ],
            timeline: t('futurePlans.products.timeline')
        },
        {
            icon: <FaCogs />,
            titleKey: 'futurePlans.technology.title',
            descKey: 'futurePlans.technology.desc',
            features: [
                t('futurePlans.technology.feature1'),
                t('futurePlans.technology.feature2'),
                t('futurePlans.technology.feature3'),
                t('futurePlans.technology.feature4')
            ],
            timeline: t('futurePlans.technology.timeline')
        },
        {
            icon: <MdPeople />,
            titleKey: 'futurePlans.member.title',
            descKey: 'futurePlans.member.desc',
            features: [
                t('futurePlans.member.feature1'),
                t('futurePlans.member.feature2'),
                t('futurePlans.member.feature3'),
                t('futurePlans.member.feature4')
            ],
            timeline: t('futurePlans.member.timeline')
        },
        {
            icon: <MdSchool />,
            titleKey: 'futurePlans.training.title',
            descKey: 'futurePlans.training.desc',
            features: [
                t('futurePlans.training.feature1'),
                t('futurePlans.training.feature2'),
                t('futurePlans.training.feature3'),
                t('futurePlans.training.feature4')
            ],
            timeline: t('futurePlans.training.timeline')
        }
    ];

    const roadmap = [
        { year: '2025', title: t('futurePlans.roadmap.year1Title'), desc: t('futurePlans.roadmap.year1Desc') },
        { year: '2026', title: t('futurePlans.roadmap.year2Title'), desc: t('futurePlans.roadmap.year2Desc') },
        { year: '2027', title: t('futurePlans.roadmap.year3Title'), desc: t('futurePlans.roadmap.year3Desc') },
        { year: '2028+', title: t('futurePlans.roadmap.year4Title'), desc: t('futurePlans.roadmap.year4Desc') }
    ];

    return (
        <div className="min-h-screen bg-gray-50 font-sans text-gray-800">
            {/* 1. Hero Section */}
            <section className="relative py-24 bg-gradient-to-r from-slate-900 to-slate-800 text-white overflow-hidden">
                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px]"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <span className="text-secondary font-bold tracking-widest uppercase text-sm mb-4 block">{t('futurePlans.badge')}</span>
                    <h1 className="text-4xl md:text-5xl font-bold mb-6 font-display">{t('futurePlans.title')}</h1>
                    <div className="w-24 h-1 bg-secondary mx-auto mb-6"></div>
                    <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
                        {t('futurePlans.subtitle')}
                    </p>
                </div>
            </section>

            {/* 2. Vision Statement */}
            <section className="py-16 bg-white relative -mt-10 z-20 mx-4 md:mx-0">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white p-10 rounded-xl shadow-xl border-t-4 border-secondary text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-5">
                            <FaRocket className="text-9xl text-primary" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('futurePlans.vision.title')}</h2>
                        <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto italic">
                            "{t('futurePlans.vision.description')}"
                        </p>
                    </div>
                </div>
            </section>

            {/* 3. Strategic Plans Grid */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <span className="text-secondary font-bold tracking-widest uppercase text-sm mb-2 block">{t('futurePlans.strategicBadge')}</span>
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('futurePlans.strategic.title')}</h2>
                        <div className="w-16 h-1 bg-secondary mx-auto"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {plans.map((plan, index) => (
                            <div key={index} className="bg-white rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 flex flex-col h-full">
                                <div className="p-8 flex-grow">
                                    <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center text-primary text-3xl mb-6">
                                        {plan.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3">{t(plan.titleKey)}</h3>
                                    <p className="text-gray-600 mb-6 text-sm leading-relaxed">{t(plan.descKey)}</p>

                                    <ul className="space-y-3">
                                        {plan.features.map((feature, idx) => (
                                            <li key={idx} className="flex items-start gap-3">
                                                <FaCheckCircle className="text-secondary mt-1 flex-shrink-0 text-xs" />
                                                <span className="text-gray-700 text-sm">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="px-8 py-4 bg-gray-50 border-t border-gray-100 rounded-b-xl flex justify-between items-center text-sm font-medium text-gray-500">
                                    <span className="flex items-center gap-2">
                                        <MdTimeline className="text-primary" />
                                        {t('futurePlans.targetLabel')}: {plan.timeline}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 4. Roadmap Timeline */}
            <section className="py-20 bg-slate-900 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px]"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-16">
                        <span className="text-secondary font-bold tracking-widest uppercase text-sm mb-2 block">{t('futurePlans.roadmapBadge')}</span>
                        <h2 className="text-3xl font-bold mb-4 text-white">{t('futurePlans.roadmap.title')}</h2>
                        <div className="w-16 h-1 bg-secondary mx-auto"></div>
                    </div>

                    <div className="relative">
                        {/* Timeline Line */}
                        <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-white/20"></div>

                        <div className="space-y-12">
                            {roadmap.map((item, index) => (
                                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                                    <div className={`flex-1 md:pr-12 ${index % 2 !== 0 ? 'hidden md:block' : ''}`}>
                                        {index % 2 === 0 && (
                                            <div className="bg-white/5 backdrop-blur-sm p-8 rounded-xl border border-white/10 hover:bg-white/10 transition-colors text-right md:text-right text-left">
                                                <div className="text-secondary font-bold text-2xl mb-2">{item.year}</div>
                                                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                                                <p className="text-blue-100 text-sm leading-relaxed">{item.desc}</p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="hidden md:flex w-10 h-10 bg-secondary rounded-full items-center justify-center z-10 flex-shrink-0 border-4 border-slate-900">
                                        <FaRocket className="text-slate-900 text-sm" />
                                    </div>

                                    <div className={`flex-1 md:pl-12 ${index % 2 === 0 ? 'hidden md:block' : ''}`}>
                                        {index % 2 !== 0 && (
                                            <div className="bg-white/5 backdrop-blur-sm p-8 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                                                <div className="text-secondary font-bold text-2xl mb-2">{item.year}</div>
                                                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                                                <p className="text-blue-100 text-sm leading-relaxed">{item.desc}</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Mobile View */}
                                    <div className="md:hidden w-full bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="text-secondary font-bold text-xl">{item.year}</div>
                                            <div className="h-0.5 flex-grow bg-white/10"></div>
                                        </div>
                                        <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                                        <p className="text-blue-100 text-sm">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FuturePlans;
